import { Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useGlobalPresence } from "@/hooks/useGlobalPresence";
import { useGuest } from "@/contexts/GuestContext";
import { useEffect, useRef } from "react";
import { logSecurityEvent } from "@/utils/securityLogger";

export const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading, isGuest } = useAuth();
  const { isGuest: isGuestFromContext } = useGuest();
  const hasLoggedAttempt = useRef(false);
  
  // Track global presence for all authenticated users (not guests)
  useGlobalPresence(user?.id);

  // Check if user has completed onboarding
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['profile-onboarding-check', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data } = await supabase
        .from('profiles')
        .select('onboarding_completed, is_suspended, suspension_reason, suspended_at')
        .eq('id', user.id)
        .single();
      return data;
    },
    enabled: !!user,
  });

  // Log suspended access attempts
  useEffect(() => {
    if (
      profile?.is_suspended && 
      user?.id && 
      !hasLoggedAttempt.current &&
      window.location.pathname !== '/account-suspended'
    ) {
      hasLoggedAttempt.current = true;
      logSecurityEvent(
        'suspended_access_attempt',
        user.id,
        `Suspended user attempted to access: ${window.location.pathname}`,
        { attempted_path: window.location.pathname }
      );
    }
  }, [profile?.is_suspended, user?.id]);

  if (loading || profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Allow guests to access the app
  if (isGuestFromContext) {
    return <>{children}</>;
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Redirect suspended users to suspension page
  if (profile?.is_suspended && window.location.pathname !== '/account-suspended') {
    return <Navigate to="/account-suspended" replace />;
  }

  // Redirect to onboarding if not completed
  if (profile && !profile.onboarding_completed && window.location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
};
